import React, { Suspense, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { useCart } from './context/CartContext';
import Header from './components/Header';
import HomeScreen from './components/HomeScreen';
import SearchModal from './components/SearchModal';
import ProductGrid from './components/ProductGrid';
import GoogleAuthCallback from './components/GoogleAuthCallback';
import BottomNav from './components/BottomNav';
import InstallPwaGate from './components/InstallPwaGate';
import GoogleAnalytics from './components/GoogleAnalytics';
import AdSlot from './components/AdSlot';
import Footer from './components/Footer';
import { useI18n } from './context/I18nContext';
import { FavoritesProvider } from './context/FavoritesContext';
import './App.css';

const ProductDetail = React.lazy(() => import('./components/ProductDetail'));
const Cart = React.lazy(() => import('./components/Cart'));
const FavoritesPage = React.lazy(() => import('./components/FavoritesPage'));
const ContactPage = React.lazy(() => import('./components/ContactPage'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const AccountScreen = React.lazy(() => import('./components/AccountScreen'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      retry: 1,
    },
  },
});

const CartToast = () => {
  const { lastAdded } = useCart();
  const { t } = useI18n();
  const [anchorPosition, setAnchorPosition] = useState({ x: 0, y: 0 });

  useLayoutEffect(() => {
    if (!lastAdded) return undefined;

    const updateAnchorPosition = () => {
      const anchors = Array.from(document.querySelectorAll('[data-cart-anchor="true"]'));
      const visibleAnchor = anchors.find((anchor) => {
        const rect = anchor.getBoundingClientRect();
        const style = window.getComputedStyle(anchor);
        return rect.width > 0 && rect.height > 0 && style.display !== 'none' && style.visibility !== 'hidden';
      });

      if (!visibleAnchor) return;

      const rect = visibleAnchor.getBoundingClientRect();
      setAnchorPosition({
        x: rect.right,
        y: rect.bottom,
      });
    };

    updateAnchorPosition();
    window.addEventListener('resize', updateAnchorPosition);
    window.addEventListener('scroll', updateAnchorPosition, true);

    return () => {
      window.removeEventListener('resize', updateAnchorPosition);
      window.removeEventListener('scroll', updateAnchorPosition, true);
    };
  }, [lastAdded]);

  if (!lastAdded) {
    return null;
  }

  return (
    <div
      key={lastAdded.id}
      className="cart-toast cart-toast--visible"
      role="status"
      aria-live="polite"
      style={{
        '--cart-target-x': `${anchorPosition.x}px`,
        '--cart-target-y': `${anchorPosition.y}px`,
      }}
    >
      <svg className="cart-toast-icon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M3 4H5L7 14H18L21 7H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="9" cy="19" r="1.5" fill="currentColor" />
        <circle cx="18" cy="19" r="1.5" fill="currentColor" />
      </svg>
      <span className="cart-toast-label">{t('productTile.addedToCart')}</span>
      {lastAdded && (
        <span className="cart-toast-name">{lastAdded.product?.name}</span>
      )}
    </div>
  );
};

const ADS_LEFT_SLOT = process.env.REACT_APP_ADSENSE_SLOT_LEFT || '';
const ADS_RIGHT_SLOT = process.env.REACT_APP_ADSENSE_SLOT_RIGHT || '';
const ADS_MOBILE_SLOT = process.env.REACT_APP_ADSENSE_SLOT_MOBILE || '';
const ADS_CLIENT = process.env.REACT_APP_ADSENSE_CLIENT_ID || '';
const HAS_DESKTOP_ADS = Boolean(ADS_CLIENT && ADS_LEFT_SLOT && ADS_RIGHT_SLOT);

const AppRoutes = ({
  searchTerm,
  selectedCategory,
  resetSignal,
  openCategoryMenuSignal,
  handleSearch,
  handleCategorySelect,
  handleCartClick,
  handleCloseCart,
  isCartOpen,
  onScanClick,
  onSearchClick,
}) => {
  const location = useLocation();

  // Directional page transitions: track history index to detect forward vs back
  const prevIdxRef = useRef(window.history.state?.idx ?? 0);
  const dirRef = useRef('forward');
  const currentIdx = window.history.state?.idx ?? 0;
  if (currentIdx !== prevIdxRef.current) {
    dirRef.current = currentIdx < prevIdxRef.current ? 'back' : 'forward';
    prevIdxRef.current = currentIdx;
  }
  const dir = dirRef.current;

  // Scroll position restoration
  useEffect(() => {
    if (dir === 'back') {
      const saved = sessionStorage.getItem(`scroll_${location.pathname}`);
      if (saved) {
        requestAnimationFrame(() => window.scrollTo({ top: parseInt(saved, 10), behavior: 'instant' }));
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'instant' });
    }
    return () => {
      sessionStorage.setItem(`scroll_${location.pathname}`, String(window.scrollY));
    };
  }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div key={location.pathname} className={`route-fade route-${dir}`}>
        <Suspense fallback={null}>
        <Routes>
          <Route
            path="/"
            element={(
              <HomeScreen
                onSearch={handleSearch}
                onCategorySelect={handleCategorySelect}
                onCartClick={handleCartClick}
                onScanClick={onScanClick}
                onSearchClick={onSearchClick}
              />
            )}
          />
          <Route
            path="/products"
            element={(
              <ProductGrid
                searchTerm={searchTerm}
                selectedCategory={selectedCategory}
                resetSignal={resetSignal}
                openCategoryMenuSignal={openCategoryMenuSignal}
              />
            )}
          />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/account" element={<AccountScreen />} />
          <Route path="/favorites" element={<FavoritesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/auth/google/callback" element={<GoogleAuthCallback />} />
          <Route path="/auth/google/native/callback" element={<GoogleAuthCallback />} />
        </Routes>
        </Suspense>
      </div>
      {isCartOpen && <Suspense fallback={null}><Cart onClose={handleCloseCart} /></Suspense>}
    </>
  );
};

const AppLayout = ({
  searchTerm,
  selectedCategory,
  resetSignal,
  openCategoryMenuSignal,
  handleSearch,
  handleCategorySelect,
  isCartOpen,
  handleCloseCart,
  handleCartClick,
  handleLogoClick,
  handleOpenCategoryMenu,
}) => {
  const { t } = useI18n();
  const location = useLocation();
  const isHome = location.pathname === '/';
  const isProductDetail = /^\/product\//.test(location.pathname);
  const isAccount = location.pathname === '/account';
  const isFavorites = location.pathname === '/favorites';
  const isFullscreen = isProductDetail || isAccount || isFavorites;
  const showDesktopAds = HAS_DESKTOP_ADS && !isHome && !isFullscreen;

  // Counter-based trigger so HomeScreen scan tile opens the BottomNav scanner
  const [scanTrigger, setScanTrigger] = useState(0);
  const handleScanClick = () => setScanTrigger((n) => n + 1);

  // Full-screen search modal
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const navigate = useNavigate();

  // iOS-style swipe-back from left edge
  useEffect(() => {
    let startX = 0, startY = 0, edgeSwipe = false;
    const onStart = (e) => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      edgeSwipe = startX < 22;
    };
    const onEnd = (e) => {
      if (!edgeSwipe) return;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = Math.abs(e.changedTouches[0].clientY - startY);
      if (dx > 72 && dx > dy * 1.8) navigate(-1);
      edgeSwipe = false;
    };
    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchend', onEnd);
    };
  }, [navigate]);
  const handleOpenSearch = () => setIsSearchOpen(true);
  const handleSearchSubmit = (term) => {
    handleSearch(term);
    navigate('/products');
  };

  return (
    <div className="App">
      <GoogleAnalytics />
      <Analytics />
      <InstallPwaGate />
      {!isHome && !isFullscreen && (
        <Header
          searchTerm={searchTerm}
          onSearch={handleSearch}
          onCartClick={handleCartClick}
          onLogoClick={handleLogoClick}
          isCartOpen={isCartOpen}
          onHeaderInteract={handleCloseCart}
        />
      )}
      {!isHome && !isFullscreen && (
        <div className="mobile-ad-wrap">
          <AdSlot
            slot={ADS_MOBILE_SLOT}
            className="ad-slot--mobile"
            format="horizontal"
            label={t('app.adLabel')}
          />
        </div>
      )}
      <div className={`app-shell ${showDesktopAds ? 'app-shell--with-rails' : 'app-shell--single'}`}>
        {showDesktopAds && (
          <aside className="desktop-ad-rail desktop-ad-rail-left">
            <AdSlot
              slot={ADS_LEFT_SLOT}
              className="ad-slot--rail"
              format="rectangle"
              responsive={false}
              label={t('app.adLabel')}
            />
          </aside>
        )}
        <main className={`main-content${isHome ? ' main-content--home' : ''}${isFullscreen ? ' main-content--pd' : ''}`}>
          <AppRoutes
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            resetSignal={resetSignal}
            openCategoryMenuSignal={openCategoryMenuSignal}
            handleSearch={handleSearch}
            handleCategorySelect={handleCategorySelect}
            handleCartClick={handleCartClick}
            isCartOpen={isCartOpen}
            handleCloseCart={handleCloseCart}
            onScanClick={handleScanClick}
            onSearchClick={handleOpenSearch}
          />
        </main>
        {showDesktopAds && (
          <aside className="desktop-ad-rail desktop-ad-rail-right">
            <AdSlot
              slot={ADS_RIGHT_SLOT}
              className="ad-slot--rail"
              format="rectangle"
              responsive={false}
              label={t('app.adLabel')}
            />
          </aside>
        )}
      </div>
      {!isHome && !isFullscreen && <Footer />}
      <CartToast />
      <BottomNav
        onCartClick={handleCartClick}
        onOpenCategoryMenu={handleOpenCategoryMenu}
        onSearchClick={handleOpenSearch}
        isCartOpen={isCartOpen}
        onCloseCart={handleCloseCart}
        scanTrigger={scanTrigger}
      />
      <SearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
        onSubmit={handleSearchSubmit}
        onScanClick={handleScanClick}
      />
    </div>
  );
};

function App() {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [resetSignal, setResetSignal] = useState(0);
  const [openCategoryMenuSignal, setOpenCategoryMenuSignal] = useState(0);
  const isNative = Boolean(window.Capacitor?.isNativePlatform());

  // Apply native-specific class and status bar color.
  useEffect(() => {
    if (!isNative) return;
    document.documentElement.classList.add('capacitor-app');

    const applyStatusBar = async () => {
      const { StatusBar, Style } = await import('@capacitor/status-bar');
      await StatusBar.setStyle({ style: Style.Light });
      await StatusBar.setBackgroundColor({ color: '#f8f9fa' });
    };
    applyStatusBar().catch(() => {});
  }, [isNative]);

  useEffect(() => {
    // Native apps handle back navigation themselves; this lock is PWA-only.
    if (isNative) return undefined;

    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;

    if (!isStandalone) return undefined;

    const lockBackNavigation = () => {
      window.history.pushState({ pwaBackLock: true }, '', window.location.href);
    };

    window.history.pushState({ pwaBackLock: true }, '', window.location.href);
    window.addEventListener('popstate', lockBackNavigation);

    return () => {
      window.removeEventListener('popstate', lockBackNavigation);
    };
  }, [isNative]);

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category || '');
  };

  const handleCartClick = () => {
    setIsCartOpen((current) => !current);
  };

  const handleCloseCart = () => {
    setIsCartOpen(false);
  };

  const handleLogoClick = () => {
    setSearchTerm('');
    setSelectedCategory('');
    setResetSignal((prev) => prev + 1);
  };

  const handleOpenCategoryMenu = () => {
    setOpenCategoryMenuSignal((prev) => prev + 1);
  };

  return (
    <QueryClientProvider client={queryClient}>
    <HelmetProvider>
      <AuthProvider>
        <CartProvider>
          <FavoritesProvider>
          <Router>
            <AppLayout
            searchTerm={searchTerm}
            selectedCategory={selectedCategory}
            resetSignal={resetSignal}
            openCategoryMenuSignal={openCategoryMenuSignal}
            handleSearch={handleSearch}
            handleCategorySelect={handleCategorySelect}
            isCartOpen={isCartOpen}
            handleCloseCart={handleCloseCart}
            handleCartClick={handleCartClick}
            handleLogoClick={handleLogoClick}
            handleOpenCategoryMenu={handleOpenCategoryMenu}
            />
          </Router>
          </FavoritesProvider>
        </CartProvider>
      </AuthProvider>
    </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
